import React, { useMemo } from "react";
import { useApiQuery } from "@/lib/useApiQuery";
import { useQueryClient } from "@tanstack/react-query";
import { downloadCsv } from "@/lib/csv";
import { AlertTriangle, Download } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useProject } from "@/context/ProjectContext";
import { statusOf } from "@/lib/baselineStatus";
import { BaselineRow } from "@/components/energy/BaselineRow";
import { PortfolioTargetCard } from "@/components/energy/PortfolioTargetCard";

function PortfolioChip({ delta }) {
  return (
    <span
      className="text-sm font-heading font-semibold px-3 py-1.5 rounded-md border"
      data-testid="baseline-portfolio-delta"
      style={{
        color: delta > 10 ? "#B91C1C" : delta < -10 ? "#156F5B" : "#92400E",
        borderColor: "#E5E7EB",
        background: "#FFFFFF",
      }}
    >
      Portfolio {delta > 0 ? "+" : ""}{delta}%
    </span>
  );
}

function WasteBanner({ count, thresholdPct }) {
  return (
    <div className="mt-4 flex items-start gap-3 p-4 rounded-lg border border-[#FECACA] bg-[#FEF2F2]" data-testid="baseline-waste-banner">
      <AlertTriangle className="h-4 w-4 text-[#B91C1C] mt-0.5 shrink-0" />
      <div className="text-sm text-[#7F1D1D]">
        <span className="font-semibold">{count} site{count > 1 ? "s" : ""}</span> using more than {thresholdPct}% above baseline — worth investigating.
      </div>
    </div>
  );
}

/** Current usage vs the preceding period, per site, with reduction targets. */
export function BaselinesPanel({ days }) {
  const { user } = useAuth();
  const { project } = useProject();
  const queryClient = useQueryClient();
  const isAdmin = user?.role === "admin";
  const params = useMemo(() => ({ days, ...(project ? { project } : {}) }), [days, project]);
  const { data, loading, err } = useApiQuery(["baselines", params], "/energy/baselines", { params });
  const reload = () => queryClient.invalidateQueries({ queryKey: ["baselines"] });

  const rows = useMemo(() => data?.sites || [], [data]);
  const max = useMemo(
    () => Math.max(0, ...rows.flatMap((r) => [r.current_kwh || 0, r.baseline_kwh || 0])),
    [rows]
  );
  const portfolio = data?.portfolio_delta_pct;

  const exportCsv = () =>
    downloadCsv(
      `energy_baselines_${days}d.csv`,
      ["Site", "Current (kWh)", "Baseline (kWh)", "Change (%)", "Target (%)", "Target (kWh)", "Status"],
      rows.map((r) => [r.site_dis, r.current_kwh ?? "", r.baseline_kwh ?? "", r.delta_pct ?? "",
                       r.target_pct ?? "", r.target_kwh ?? "", statusOf(r.status).label])
    );

  return (
    <div className="mt-10" data-testid="energy-baselines">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-heading font-bold text-[#212121]">Baselines</h2>
          <p className="text-sm text-[#6B7280] mt-1">
            Consumption over the latest {days} day{days > 1 ? "s" : ""} of data compared with the preceding {days}-day baseline.
            {rows[0]?.data_through && (
              <span className="text-[#9CA3AF]"> Data through {new Date(rows[0].data_through).toLocaleDateString()}.</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {portfolio != null && <PortfolioChip delta={portfolio} />}
          <button onClick={exportCsv} disabled={loading || rows.length === 0} data-testid="baseline-export-btn" className="k-btn-outline">
            <Download className="h-3 w-3" /> CSV
          </button>
        </div>
      </div>

      <div className="mt-4">
        <PortfolioTargetCard data={data} days={days} isAdmin={isAdmin} onSaved={reload} />
      </div>

      {data?.wasting_count > 0 && <WasteBanner count={data.wasting_count} thresholdPct={data.threshold_pct} />}

      <div className="mt-4 k-card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center k-sublabel">Loading baselines…</div>
        ) : err ? (
          <div className="p-6 text-sm text-red-600">{err}</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#6B7280]">No consumption meters available for baseline comparison.</div>
        ) : (
          rows.map((r) => <BaselineRow key={r.site_id} row={r} max={max} isAdmin={isAdmin} onSaved={reload} />)
        )}
      </div>
    </div>
  );
}
